import "core-js/modules/es6.promise";
import "core-js/modules/es6.object.to-string";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { CodedError } from '@unimodules/core';
import { FlipType } from "./ImageManipulator.types";

function resampleSingle(canvas, width, height) {
  var resizeCanvas = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  var widthSource = canvas.width;
  var heightSource = canvas.height;
  width = Math.round(width);
  height = Math.round(height);
  var wRatio = widthSource / width;
  var hRatio = heightSource / height;
  var wRatioHalf = Math.ceil(wRatio / 2);
  var hRatioHalf = Math.ceil(hRatio / 2);
  var ctx = getContext(canvas);
  var img = ctx.getImageData(0, 0, widthSource, heightSource);
  var img2 = ctx.createImageData(width, height);
  var data = img.data;
  var data2 = img2.data;

  for (var j = 0; j < height; j++) {
    for (var i = 0; i < width; i++) {
      var x2 = (i + j * width) * 4;
      var weight = 0;
      var weights = 0;
      var weightsAlpha = 0;
      var gx_r = 0;
      var gx_g = 0;
      var gx_b = 0;
      var gx_a = 0;
      var yCenter = (j + 0.5) * hRatio;
      var yy_start = Math.floor(j * hRatio);
      var yy_stop = Math.ceil((j + 1) * hRatio);

      for (var yy = yy_start; yy < yy_stop; yy++) {
        var dy = Math.abs(yCenter - (yy + 0.5)) / hRatioHalf;
        var center_x = (i + 0.5) * wRatio;
        var w0 = dy * dy;
        var xx_start = Math.floor(i * wRatio);
        var xx_stop = Math.ceil((i + 1) * wRatio);

        for (var xx = xx_start; xx < xx_stop; xx++) {
          var dx = Math.abs(center_x - (xx + 0.5)) / wRatioHalf;
          var w = Math.sqrt(w0 + dx * dx);

          if (w >= 1) {
            continue;
          }

          weight = 2 * w * w * w - 3 * w * w + 1;
          var xPosition = 4 * (xx + yy * widthSource);
          gx_a += weight * data[xPosition + 3];
          weightsAlpha += weight;

          if (data[xPosition + 3] < 255) {
            weight = weight * data[xPosition + 3] / 250;
          }

          gx_r += weight * data[xPosition];
          gx_g += weight * data[xPosition + 1];
          gx_b += weight * data[xPosition + 2];
          weights += weight;
        }
      }

      data2[x2] = gx_r / weights;
      data2[x2 + 1] = gx_g / weights;
      data2[x2 + 2] = gx_b / weights;
      data2[x2 + 3] = gx_a / weightsAlpha;
    }
  }

  if (resizeCanvas) {
    canvas.width = width;
    canvas.height = height;
  } else {
    ctx.clearRect(0, 0, widthSource, heightSource);
  }

  ctx.putImageData(img2, 0, 0);
}

function sizeFromAngle(width, height, angle) {
  var radians = angle * Math.PI / 180;
  var c = Math.cos(radians);
  var s = Math.sin(radians);

  if (s < 0) {
    s = -s;
  }

  if (c < 0) {
    c = -c;
  }

  return {
    width: height * s + width * c,
    height: height * c + width * s
  };
}

function cropImage(canvas, image) {
  var x = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  var y = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  var width = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 0;
  var height = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : 0;
  var context = getContext(canvas);
  context.save();
  context.drawImage(image, x, y, width, height, 0, 0, width, height);
}

function drawImage(canvas, img) {
  var x = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  var y = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  var angle = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 0;
  var xFlip = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : false;
  var yFlip = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : false;
  var width = arguments.length > 7 ? arguments[7] : undefined;
  var height = arguments.length > 8 ? arguments[8] : undefined;
  var context = getContext(canvas);
  context.save();

  if (width == null) {
    width = img.naturalWidth;
  }

  if (height == null) {
    height = img.naturalHeight;
  }

  context.translate(x + canvas.width / 2, y + canvas.height / 2);
  var radians = 2 * Math.PI - angle * Math.PI / 180;
  context.rotate(radians);
  var xScale = xFlip ? -1 : 1;
  var yScale = yFlip ? -1 : 1;
  context.scale(xScale, yScale);
  context.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);
  context.restore();
  return context;
}

function getContext(canvas) {
  var ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new CodedError('ERR_IMAGE_MANIPULATOR', 'Failed to create canvas context');
  }

  return ctx;
}

function getResults(canvas, options) {
  var base64;

  if (options) {
    var _options$format = options.format,
        format = _options$format === void 0 ? 'png' : _options$format;

    if (options.format === 'png' && options.compress !== undefined) {
      console.warn('compress is not supported with png format.');
    }

    var quality = Math.min(1, Math.max(0, options.compress || 1));
    base64 = canvas.toDataURL('image/' + format, quality);
  } else {
    base64 = canvas.toDataURL();
  }

  return {
    uri: base64,
    width: canvas.width,
    height: canvas.height,
    base64: base64
  };
}

function loadImageAsync(uri) {
  return new Promise(function (resolve, reject) {
    var imageSource = new Image();

    imageSource.onload = function () {
      return resolve(imageSource);
    };

    imageSource.onerror = function () {
      return reject(imageSource);
    };

    imageSource.src = uri;
  });
}

function manipulateWithActionAsync(uri, action, options) {
  var canvas, imageSource, crop, _crop$originX, originX, _crop$originY, originY, _crop$width, width, _crop$height, height, clamp, resize, _width, _height, imageRatio, requestedWidth, requestedHeight, context, flip, xFlip, yFlip, rotate, _sizeFromAngle, _width2, _height2, _context;

  return _regeneratorRuntime.async(function manipulateWithActionAsync$(_context2) {
    while (1) {
      switch (_context2.prev = _context2.next) {
        case 0:
          canvas = document.createElement('canvas');
          _context2.next = 3;
          return _regeneratorRuntime.awrap(loadImageAsync(uri));

        case 3:
          imageSource = _context2.sent;
          canvas.width = imageSource.naturalWidth;
          canvas.height = imageSource.naturalHeight;

          if (!action.crop) {
            _context2.next = 23;
            break;
          }

          crop = action.crop;
          _crop$originX = crop.originX, originX = _crop$originX === void 0 ? 0 : _crop$originX, _crop$originY = crop.originY, originY = _crop$originY === void 0 ? 0 : _crop$originY, _crop$width = crop.width, width = _crop$width === void 0 ? 0 : _crop$width, _crop$height = crop.height, height = _crop$height === void 0 ? 0 : _crop$height;

          clamp = function clamp(value, max) {
            return Math.max(0, Math.min(max, value));
          };

          width = clamp(width, canvas.width);
          height = clamp(height, canvas.height);
          originX = clamp(originX, canvas.width);
          originY = clamp(originY, canvas.height);
          width = Math.min(originX + width, canvas.width);
          height = Math.min(originY + height, canvas.height);

          if (!(originX - width === 0 || originY - height === 0)) {
            _context2.next = 18;
            break;
          }

          throw new CodedError('ERR_IMAGE_MANIPULATOR_CROP', 'Crop size must be greater than 0: ' + JSON.stringify(crop, null, 2));

        case 18:
          canvas.width = width;
          canvas.height = height;
          cropImage(canvas, imageSource, originX, originY, width, height);
          _context2.next = 24;
          break;

        case 23:
          if (action.resize) {
            resize = action.resize;
            _width = resize.width, _height = resize.height;
            imageRatio = imageSource.naturalWidth / imageSource.naturalHeight;
            requestedWidth = 0;
            requestedHeight = 0;

            if (_width !== undefined) {
              requestedWidth = _width;
              requestedHeight = requestedWidth / imageRatio;
            }

            if (_height !== undefined) {
              requestedHeight = _height;

              if (requestedWidth === 0) {
                requestedWidth = requestedHeight * imageRatio;
              }
            }

            context = getContext(canvas);
            context.save();
            context.drawImage(imageSource, 0, 0, imageSource.naturalWidth, imageSource.naturalHeight);
            resampleSingle(canvas, requestedWidth, requestedHeight, true);
          } else if (action.flip !== undefined) {
            flip = action.flip;
            xFlip = flip === FlipType.Horizontal;
            yFlip = flip === FlipType.Vertical;
            drawImage(canvas, imageSource, 0, 0, 0, xFlip, yFlip);
          } else if (action.rotate !== undefined) {
            rotate = action.rotate;
            _sizeFromAngle = sizeFromAngle(imageSource.naturalWidth, imageSource.naturalHeight, rotate), _width2 = _sizeFromAngle.width, _height2 = _sizeFromAngle.height;
            canvas.width = _width2;
            canvas.height = _height2;
            drawImage(canvas, imageSource, 0, 0, rotate, false, false, _width2, _height2);
          } else {
            _context = getContext(canvas);

            _context.save();

            _context.drawImage(imageSource, 0, 0, imageSource.naturalWidth, imageSource.naturalHeight);
          }

        case 24:
          return _context2.abrupt("return", getResults(canvas, options));

        case 25:
        case "end":
          return _context2.stop();
      }
    }
  }, null, null, null, Promise);
}

export default {
  get name() {
    return 'ExpoImageManipulator';
  },

  manipulateAsync: function manipulateAsync(uri) {
    var actions,
        options,
        canvas,
        imageSource,
        ctx,
        output,
        i,
        action,
        _options,
        _args2 = arguments;

    return _regeneratorRuntime.async(function manipulateAsync$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            actions = _args2.length > 1 && _args2[1] !== undefined ? _args2[1] : [];
            options = _args2.length > 2 ? _args2[2] : undefined;

            if (actions.length) {
              _context3.next = 14;
              break;
            }

            canvas = document.createElement('canvas');
            _context3.next = 6;
            return _regeneratorRuntime.awrap(loadImageAsync(uri));

          case 6:
            imageSource = _context3.sent;
            canvas.width = imageSource.naturalWidth;
            canvas.height = imageSource.naturalHeight;
            ctx = getContext(canvas);
            ctx.drawImage(imageSource, 0, 0, imageSource.naturalWidth, imageSource.naturalHeight);
            return _context3.abrupt("return", getResults(canvas, options));

          case 14:
            i = 0;

          case 15:
            if (!(i < actions.length)) {
              _context3.next = 25;
              break;
            }

            action = actions[i];
            _options = void 0;

            if (i === actions.length - 1) {
              _options = options;
            }

            _context3.next = 21;
            return _regeneratorRuntime.awrap(manipulateWithActionAsync(uri || output.uri, action, _options));

          case 21:
            output = _context3.sent;

          case 22:
            i++;
            _context3.next = 15;
            break;

          case 25:
            return _context3.abrupt("return", output);

          case 26:
          case "end":
            return _context3.stop();
        }
      }
    }, null, null, null, Promise);
  }
};